<template>
  <div :id="'line-chart-container-' + dataName" class="mb-10">
    <div :id="'line-chart-' + dataName" class="line-chart"></div>
    <div v-if="debug">
      <hr />
      <table>
        <tr v-for="item in debugItems" :key="item">
          <td>
            <span class="me-5">{{ item }}:</span>
          </td>
          <td width="100%">
            <pre>{{ _self[item] }}</pre>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { DateTime } from 'luxon'
import { segmentsColors } from '@/puls-color-theme'
import { d3Locales } from '@/helpers/d3-localization'

export default {
  name: 'LineChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    dataName: {
      type: String,
      default: ''
    },
    options: {
      type: Object,
      default: () => {}
    },
    series: {
      type: Array,
      default: () => []
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      margin: { top: 20, right: 30, bottom: 40, left: 30 },
      transitionTime: 350,
      debugItems: ['width', 'height', 'ticks', 'domain', 'options'],
      svg: null,
      line: null,
      x: null,
      y: null
    }
  },
  computed: {
    width() {
      const defaultWidth = this.options.width
        ? this.options.width - this.margin.left - this.margin.right
        : 640
      return defaultWidth - this.margin.left - this.margin.right
    },
    height() {
      const defaultHeight = this.options.height
        ? this.options.height - this.margin.top - this.margin.bottom
        : 280
      return defaultHeight - this.margin.top - this.margin.bottom
    },
    domain() {
      return this.options && this.options.domain ? this.options.domain : [0, 10]
    },
    seriesWithoutTrend() {
      return this.series.filter((s) => s.name !== 'linearRegressionValue')
    },
    ticks() {
      return this.options && this.options.ticks
        ? this.options.ticks
        : this.domain[1] - this.domain[0]
    },
    linearRegressionColor() {
      return 'var(--kt-gray-400)'
    },
    myColor() {
      return d3
        .scaleOrdinal()
        .domain(this.seriesWithoutTrend.map((g) => g.name))
        .range(segmentsColors)
    },
    fractionalDigits() {
      if (Object.keys(this.options).includes('fractionalDigits')) {
        return this.options.fractionalDigits
      } else if (this.options.percentage) {
        return 0
      } else {
        return 1
      }
    },
    dataReady() {
      const that = this
      const unSortedData = this.series.map(({ name, title }) => {
        return {
          name,
          title,
          values: this.data
            .filter((d) => {
              return Object.keys(d).includes(name)
            })
            .map(function (d) {
              let formattedValue = that.$localeNumber(
                d[name].value,
                that.fractionalDigits
              )
              if (that.options.percentage) {
                const percentageTitle =
                  that.series
                    .find((s) => s.name === that.options.percentageItem)
                    ?.title?.toLowerCase() || ''
                formattedValue =
                  that.$localeNumber(d[name].value, that.fractionalDigits) +
                  ` % ${percentageTitle}`
              }
              return { date: d.date, ...d[name], title, formattedValue }
            })
        }
      })
      const sortedData = unSortedData.map((dataObj) => {
        const values = dataObj.values.sort((a, b) => {
          const dateA = DateTime.fromISO(a.date).toISO()
          const dateB = DateTime.fromISO(b.date).toISO()
          if (dateA < dateB) {
            return 1
          } else if (dateA > dateB) {
            return -1
          }
          return 0
        })
        return { ...dataObj, values }
      })
      return sortedData
    }
  },
  watch: {
    data: function (newData, oldData) {
      // If additional data series has been added, we need to re-init the chart
      if (
        Object.keys(newData[0] || {}).length >
        Object.keys(oldData[0] || {}).length
      ) {
        this.initChart()
      }
      this.updateChart()
    },
    options: function (newOptions, oldOptions) {
      if (
        newOptions &&
        oldOptions &&
        Object.entries(newOptions).sort().toString() ===
          Object.entries(oldOptions).sort().toString()
      ) {
        return
      }
      this.updateChart()
    }
  },
  mounted() {
    this.initChart()
    this.updateChart()
  },
  beforeUnmount() {
    d3.select(`.tooltip-line-chart-container-${this.dataName}`).remove()
  },
  methods: {
    onMouseOver(d, value) {
      const tooltip = d3.select(
        `.tooltip-line-chart-container-${this.dataName}`
      )
      tooltip.transition().duration(200).style('opacity', 0.95)
      if (d.tooltip) {
        tooltip.html(d.tooltip)
      } else {
        tooltip.html(
          `<div class="p-3 tooltip-bg" style="max-width: 320px;">
            <p class="tooltip-title">
            ${value.title}
            </p>
            <table><tr><td class="d-flex align-items-start"><i class="bi bi-calendar-event fs-3 me-3"></i>
            ${DateTime.fromJSDate(value.date).toLocaleString(DateTime.DATE_FULL)}
            </td></tr><tr><td class="d-flex align-items-start"><i class="bi bi-bar-chart-fill fs-3 me-3"></i>
            ${value.formattedValue}
            </td></tr></table></div>`
        )
      }
    },
    onMouseMove(d) {
      const tooltip = d3.select(
        `.tooltip-line-chart-container-${this.dataName}`
      )
      const marginTop = 10
      const tooltipBox = tooltip.node().getBoundingClientRect()
      const tooltipOffsetX = -tooltipBox.width / 2 // half tooltip
      const tooltipOffsetY = -tooltipBox.height - marginTop
      const left = Math.max(0, d.pageX + tooltipOffsetX) // (event.layerX - tooltipBox.x)
      const top = d.pageY + tooltipOffsetY // (event.layerY - tooltipBox.y)
      tooltip.style('left', left + 'px').style('top', top + 'px')
    },
    onMouseLeave(d) {
      d3.select(`.tooltip-line-chart-container-${this.dataName}`)
        .transition()
        .duration(this.transitionTime)
        .style('opacity', 0)
        .on('end', function () {
          d3.select(this).style('left', null).style('top', null)
        })
    },
    addMouseTooltip(element) {
      element
        .on('mouseover', this.onMouseOver)
        .on('mousemove', this.onMouseMove)
        .on('mouseleave', this.onMouseLeave)
    },
    addDots() {
      const that = this

      // remove old dots
      this.svg
        .selectAll('.dots')
        .transition()
        .duration(this.transitionTime / 2)
        .style('opacity', 0)
        .remove()

      // create new group
      const dotsGroup = this.svg
        .append('g')
        .selectAll('myDots')
        .data(
          this.dataReady.filter(
            (datapoint) => datapoint.name !== 'linearRegressionValue'
          )
        )

      // add new dots
      dotsGroup
        .enter()
        .append('g')
        .attr('class', 'dots')
        .attr('stroke', function (d) {
          return that.myColor(d.name)
        })
        .attr('stroke-width', 3)
        .attr('fill', 'white')
        .selectAll('myPoints')
        .data(function (d) {
          return d.values
        })
        .enter()
        .append('circle')
        .attr('r', 0)
        .attr('class', this.dataName + ' points')
        .attr('cx', function (d) {
          return that.x(d.date)
        })
        .attr('cy', function (d) {
          return that.y(d.value)
        })
        .transition()
        .duration(this.transitionTime * 2)
        .attr('r', 5)

      this.addMouseTooltip(d3.selectAll(`.${this.dataName}.points`))
      // this.addMouseTooltip(dotsGroup)
    },
    updateChart() {
      const that = this

      // update axis
      this.x = d3.scaleTime().range([0, this.width])
      this.x.domain(
        d3.extent(this.data, function (d) {
          return d.date
        })
      )

      var xLabels = d3.axisBottom(this.x).ticks(4)

      // const xLabelX = this.options.largeFontSize ? 60 : 28
      const xLabelY = this.options.largeFontSize ? 10 : 10

      this.svg
        .selectAll('.x.linechart-axis')
        .call(xLabels)
        .selectAll('text')
        .attr('y', xLabelY)
        .attr('dy', '1em')
      // .attr('x', xLabelX)
      // .attr('transform', 'rotate(45)')

      this.y = d3
        .scaleLinear()
        .domain(this.domain)
        .range([this.height - this.margin.bottom, 0])
      var yGrid = d3
        .scaleLinear()
        .domain(this.domain)
        .range([this.height - this.margin.bottom, 0])
      this.svg
        .selectAll('.grid')
        .transition()
        .duration(this.transitionTime)
        .call(
          d3
            .axisLeft(yGrid)
            .ticks(this.ticks)
            .tickSize(-this.width)
            .tickFormat('')
        )

      this.svg
        .selectAll('.y.linechart-axis')
        .transition()
        .duration(this.transitionTime)
        .call(d3.axisLeft(that.y).ticks(this.ticks))
        .selectAll('text')
        .attr('font-size', this.options.largeFontSize ? '1.5rem' : '1.0rem')

      // add new lines
      this.line = d3
        .line()
        .curve(d3.curveCatmullRom)
        .x(function (d) {
          return that.x(+d.date)
        })
        .y(function (d) {
          return that.y(+d.value)
        })
      this.svg
        .selectAll('myLines')
        .data(this.dataReady)
        .enter()
        .append('path')
        .attr('class', function (d) {
          return d.name + ' lines'
        })
        .attr('d', function (d) {
          return that.line(d.values)
        })
        .attr('stroke', function (d) {
          if (d.name === 'linearRegressionValue') {
            return that.linearRegressionColor
          } else {
            return that.myColor(d.name)
          }
        })

        .style('fill', 'none')

      // update lines
      this.svg
        .selectAll('.lines')
        .data(this.dataReady)
        .transition()
        .duration(this.transitionTime)
        .attr('d', function (d) {
          return that.line(d.values)
        })
        .attr('stroke', function (d) {
          if (d.name === 'linearRegressionValue') {
            return that.linearRegressionColor
          } else {
            return that.myColor(d.name)
          }
        })

      // delete lines
      this.svg.selectAll('.lines').data(this.dataReady).exit().remove()

      this.addDots()
    },
    initChart() {
      const that = this

      const userLocale = localStorage.selectedLocale || 'en'
      const d3Locale = d3Locales[userLocale] || d3Locales['en']
      d3.timeFormatDefaultLocale(d3Locale)

      // If the chart already has an svg element this needs to be removed
      if (that.svg) {
        const element = document.getElementById(
          `#line-chart-svg-${this.dataName}`
        )
        element.remove()
      }

      // tooltip
      d3.select('body')
        .append('div')
        .attr('class', `tooltip tooltip-line-chart-container-${this.dataName}`)
        .style('opacity', 0.0)
        .style('pointer-events', 'none')
        .style('min-width', '300px;')
        .style('z-index', 4000)

      // append the svg object to the body of the page
      this.svg = d3
        .select(`#line-chart-${this.dataName}`)
        .append('svg')
        .attr('id', `#line-chart-svg-${this.dataName}`)
        .attr(
          'viewBox',
          `0 0 ${this.width + this.margin.left + this.margin.right} ${
            this.height
          }`
        )
        // .attr('preserveAspectRatio', `xMidYMid meet`)
        // .attr('width', width + margin.left + margin.right)
        // .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr(
          'transform',
          'translate(' + this.margin.left + ',' + this.margin.top + ')'
        )

      // Add X axis --> it is a date format
      this.x = d3.scaleTime().range([0, this.width])
      this.x.domain(
        d3.extent(this.data, function (d) {
          return d.date
        })
      )

      var xLabels = d3.axisBottom(this.x).ticks(4)

      // .tickFormat(d3.timeFormat('%d/%m'))
      // .tickValues(this.data.map((d) => d.date))
      this.svg
        .append('g')
        .attr('class', 'x linechart-axis')
        .attr(
          'transform',
          'translate(0,' + (this.height - this.margin.bottom) + ')'
        )
        .call(xLabels)
        .selectAll('text')
        .attr('y', 35)
        .attr('dy', '1em')
        // .attr('x', 30)
        // .attr('transform', 'rotate(45)')
        .attr('font-size', this.options.largeFontSize ? '1.5rem' : '1.0rem')

      // Add Y axis
      this.y = d3
        .scaleLinear()
        .domain(this.domain)
        .range([this.height - this.margin.bottom, 0])

      this.svg
        .append('g')
        .attr('class', 'y linechart-axis')
        .call(d3.axisLeft(that.y).ticks(this.ticks))
        .selectAll('text')
        .attr('font-size', this.options.largeFontSize ? '1.5rem' : '1.0rem')

      var yGrid = d3
        .scaleLinear()
        .domain(this.domain)
        .range([this.height - this.margin.bottom, 0])
      this.svg
        .append('g')
        .attr('class', 'linechart-grid')
        .call(
          d3
            .axisLeft(yGrid)
            .ticks(this.ticks)
            .tickSize(-this.width)
            .tickFormat('')
        )

      // Add the lines
      this.line = d3
        .line()
        .curve(d3.curveCatmullRom)
        .x(function (d) {
          return that.x(+d.date)
        })
        .y(function (d) {
          return that.y(+d.value)
        })
      this.svg
        .selectAll('myLines')
        .data(this.dataReady)
        .enter()
        .append('path')
        .attr('class', function (d) {
          return d.name + ' lines'
        })
        .attr('d', function (d) {
          return that.line(d.values)
        })
        .attr('stroke', function (d) {
          if (d.name === 'linearRegressionValue') {
            return that.linearRegressionColor
          } else {
            return that.myColor(d.name)
          }
        })
        .style('fill', 'none')
    }
  }
}
</script>

<style lang="scss">
.linechart-grid > path {
  stroke: white;
  fill: none;
}

.line-chart > svg {
  overflow: visible;
}

.line-chart .lines {
  stroke-dasharray: 0;
  stroke-width: 4;
  fill: none;
  &.linearRegressionValue {
    stroke-dasharray: 3, 3;
    stroke-width: 2;
    fill: none;
  }
}

.linechart-grid .lines {
  stroke-dasharray: 3, 3;
  stroke-width: 2;
  fill: none;
}

.linechart-grid > .tick > line {
  opacity: 0.7;
  stroke: lightgray;
}
.tooltip {
  position: absolute;
}
.tooltip-bg {
  background-color: var(--kt-input-bg) !important;
  border-radius: 5px;
  box-shadow: 2px 2px 2px var(--kt-table-border-color);
}
.tooltip-title {
  font-weight: 600;
}
.linechart-axis > .tick > line {
  stroke: lightgray;
  color: white;
}
.linechart-axis > .tick > text {
  color: var(--kt-headings-color);
  font-weight: 300;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
}

// .linechart-axis > .tick > text {
// font-size: 0.8rem;
// }
@media only screen and (max-device-width: 1124px) {
  .linechart-axis > .tick > text {
    font-size: 1.4rem;
  }
}
</style>
